import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { isWindowTreeless } from 'lib/treelessUtils'
import {
  type AIChatMessage,
  GUIDE,
  type Session,
} from 'pages/session/store/Session'
import { ChatAnswerFeedback } from 'tsp-types'

const initialState: Session = {
  data: {},
  status: 'IN_PROGRESS',
  formValues: {},

  view: GUIDE,
  answer: '',
  feedback: null,
  meta: {},
  isAiChatOpen: false,
  stepError: null,
  isTreelessWindow: isWindowTreeless(),
}
export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    reset: () => initialState,
    setStep: (state, { payload }: PayloadAction<Session['step']>) => {
      state.step = payload
      state.stepError = null
    },
    setStepError: (state, { payload }: PayloadAction<Session['stepError']>) => {
      state.stepError = payload
    },
    setGuideData: (state, { payload }: PayloadAction<Session['data']>) => {
      state.data = payload
    },
    setView: (state, { payload }: PayloadAction<Session['view']>) => {
      state.view = payload
    },
    setAnswer: (state, { payload }: PayloadAction<Session['answer']>) => {
      state.answer = payload
    },
    setFormValue: (
      state,
      { payload }: PayloadAction<Session['formValues']>
    ) => {
      state.formValues = { ...payload }
    },
    setClaimDetail: (
      state,
      { payload }: PayloadAction<Session['claimDetails']>
    ) => {
      state.claimDetails = { ...payload }
    },
    setFeedback: (state, { payload }: PayloadAction<Session['feedback']>) => {
      state.feedback = payload
    },
    setStatus: (state, { payload }: PayloadAction<Session['status']>) => {
      state.status = payload
    },
    setMeta: (state, { payload }: PayloadAction<Session['meta']>) => {
      state.meta = payload
    },
    setIsFormNodeValid: (
      state,
      { payload }: PayloadAction<Session['isFormNodeValid']>
    ) => {
      state.isFormNodeValid = payload
    },
    setFormSubmitStatus: (
      state,
      { payload }: PayloadAction<Session['formSubmitStatus']>
    ) => {
      state.formSubmitStatus = payload
    },
    setFormSubmitError: (
      state,
      { payload }: PayloadAction<Session['formSubmitError']>
    ) => {
      state.formSubmitError = payload
    },
    toggleAiChatSidebar: state => {
      state.isAiChatOpen = !state.isAiChatOpen
    },
    initEmptyChat: state => {
      state.aiChatState = { _id: '', messages: [] }
    },
    setChatId: (state, action: PayloadAction<{ chatId: string }>) => {
      if (!state.aiChatState) {
        throw new Error('AI chat has not been initialized.')
      }
      state.aiChatState._id = action.payload.chatId
    },
    addChatMessage(state, action: PayloadAction<AIChatMessage>) {
      if (!state.aiChatState) {
        throw new Error('AI chat has not been initialized.')
      }
      state.aiChatState.messages.push(action.payload)
    },
    setChatMessageFeedback(
      state,
      action: PayloadAction<{ id: string; value: ChatAnswerFeedback }>
    ) {
      if (!state.aiChatState) {
        throw new Error('AI chat has not been initialized.')
      }
      const messages = state.aiChatState.messages
      const idx = messages.findIndex(msg => msg.id === action.payload.id)
      if (idx !== -1) {
        messages[idx] = { ...messages[idx], feedback: action.payload.value }
      }
    },
  },
})

export const {
  reset,
  setGuideData,
  setStep,
  setStepError,
  setView,
  setAnswer,
  setFeedback,
  setStatus,
  setMeta,
  setFormValue,
  setIsFormNodeValid,
  setFormSubmitStatus,
  setFormSubmitError,
  setClaimDetail,
  toggleAiChatSidebar,
  initEmptyChat,
  setChatId,
  addChatMessage,
  setChatMessageFeedback,
} = sessionSlice.actions
export default sessionSlice.reducer
